import { type ComponentProps, useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import { usePushSubscription } from 'hooks/usePushSubscription';
import { simpleDateAtTime } from 'lib/date';
import { describeDevice, isReceiving } from 'lib/webPush';
import { PushSubscriptionRow } from 'services/api/pushSubscriptions';
import { useToastActions } from 'stores/toastStore';
import { BodySmall, H3, Label } from 'styles/typography';

const SUPPORT_EMAIL = 'support@olio.health';

type ThisDeviceState =
  | 'loading'
  | 'error'
  | 'unavailable'
  | 'install'
  | 'unsupported'
  | 'blocked'
  | 'ready'
  | 'connected';

type PushState = ReturnType<typeof usePushSubscription>;

// What the panel says about the browser the page is open in. The order matters: an iPhone tab has
// no push APIs at all, so "install first" has to win over "not supported", and a blocked browser
// stays blocked even while its old registration is still on the account.
function thisDeviceState(push: PushState): ThisDeviceState {
  if (push.loading) return 'loading';
  if (push.loadError) return 'error';
  if (!push.vapidConfigured) return 'unavailable';
  if (push.ios && !push.standalone) return 'install';
  if (!push.supported) return 'unsupported';
  if (push.permission === 'denied') return 'blocked';
  if (push.enabledOnThisDevice) return 'connected';
  return 'ready';
}

// The account's push devices (the user's, not the selected client's): connect this browser, and
// see or remove every device that receives their push notifications.
export default function PushDevicesSection() {
  const push = usePushSubscription();
  const { addToast } = useToastActions();
  const [removingId, setRemovingId] = useState<string | null>(null);

  const state = thisDeviceState(push);
  // An environment without a push keypair cannot deliver anywhere, so there is nothing to manage.
  if (state === 'unavailable') return null;

  const connect = async () => {
    try {
      const permission = await push.enable();
      if (permission === 'granted') addToast({ text: 'This device is connected to receive push notifications.' });
    } catch {
      addToast({ text: "This device couldn't be connected. Please try again." });
    }
  };

  const remove = async (device: PushSubscriptionRow) => {
    setRemovingId(device.id);
    try {
      if (device.id === push.thisDevice?.id) {
        await push.disable();
      } else {
        await push.removeDevice(device.id);
      }
    } catch {
      addToast({ text: "The device couldn't be removed. Please try again." });
    } finally {
      setRemovingId(null);
    }
  };

  // This device first, then the rest in the order they were connected.
  const devices = [...push.devices].sort(
    (a, b) => Number(b.id === push.thisDevice?.id) - Number(a.id === push.thisDevice?.id)
  );

  return (
    <Section data-testid='push-devices'>
      <StyledH3>Devices</StyledH3>
      <Description>Manage your connected devices in Olio to receive push notifications</Description>

      {state !== 'loading' && state !== 'connected' && (
        <Card data-testid={`push-device-state-${state}`}>
          <ThisDeviceMessage state={state} push={push} onConnect={connect} />
        </Card>
      )}

      {devices.map((device) => {
        const isThisDevice = device.id === push.thisDevice?.id;
        const receiving = isReceiving(device, { isThisDevice, permission: push.permission });

        return (
          <Card key={device.id} data-testid='push-device'>
            <DeviceRow>
              <DeviceText>
                <NameRow>
                  <DeviceName>{describeDevice(device.userAgent)}</DeviceName>
                  {isThisDevice && <ThisDeviceBadge>This Device</ThisDeviceBadge>}
                </NameRow>
                <Status>
                  <StatusText $receiving={receiving}>
                    {receiving ? 'Receiving Notifications' : 'Not Receiving Notifications'}
                  </StatusText>
                  <Separator>•</Separator>
                  <span>
                    {device.lastUsedAt
                      ? `Last Notification Received: ${simpleDateAtTime(device.lastUsedAt)}`
                      : 'No notifications received yet'}
                  </span>
                </Status>
              </DeviceText>
              <RemoveButton
                type='button'
                data-testid='push-device-remove'
                disabled={removingId !== null}
                onClick={() => remove(device)}>
                Remove Device
              </RemoveButton>
            </DeviceRow>
          </Card>
        );
      })}
    </Section>
  );
}

function ThisDeviceMessage({
  state,
  push,
  onConnect,
}: {
  state: ThisDeviceState;
  push: PushState;
  onConnect: () => void;
}) {
  switch (state) {
    case 'error':
      return (
        <>
          <Message>Couldn&apos;t load your devices.</Message>
          <ActionButton onClick={() => push.retryLoad()}>Try Again</ActionButton>
        </>
      );
    case 'install':
      return (
        <>
          <Message>
            <Strong>
              This device needs to be installed (added to your home screen) first to be connected to receive push
              notifications.
            </Strong>{' '}
            Follow the steps below to install Olio to your device.
          </Message>
          <Steps>
            <li>Tap the &ldquo;Share&rdquo; button within your browser</li>
            <li>Tap the &ldquo;Add to Home Screen&rdquo;</li>
            <li>Return to this page from the app you just added to your home screen</li>
          </Steps>
        </>
      );
    case 'unsupported':
      return <Muted>This device does not support push notifications and cannot be connected.</Muted>;
    case 'blocked':
      return (
        <>
          <Message>
            <BlockedLead>This device is currently blocked from connecting to Olio.</BlockedLead> This is likely being
            caused by previously clicking &ldquo;Block&rdquo; in the browser when enabling permission or you have
            disabled Olio in your phone / computer settings.
          </Message>
          <Message>
            Contact <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> for help unblocking Olio from your device.
          </Message>
        </>
      );
    default:
      return (
        <>
          <Message>This device is available to connect in Olio to start receiving push notifications.</Message>
          <ActionButton onClick={onConnect} loading={push.isEnabling} disabled={push.isEnabling}>
            Connect Device
          </ActionButton>
        </>
      );
  }
}

function ActionButton(props: ComponentProps<typeof Button>) {
  return (
    <ButtonRow>
      <Button size='xsmall' {...props} />
    </ButtonRow>
  );
}

const Section = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledH3 = styled(H3)`
  margin-bottom: 12px;
`;

const Description = styled(BodySmall)`
  font-size: 12px;
  margin-bottom: 24px;
`;

const Card = styled.div`
  border-radius: ${({ theme }) => theme.dimensions.borderRadius};
  border: 1px solid ${({ theme }) => theme.colors.black10};
  background-color: ${({ theme }) => theme.colors.white};
  margin-bottom: 16px;
  padding: 24px;
`;

const Message = styled(BodySmall)`
  display: block;
  line-height: 20px;

  & + & {
    margin-top: 16px;
  }
`;

const Muted = styled(Message)`
  color: ${({ theme }) => theme.colors.black50};
`;

const Strong = styled.span`
  font-weight: 500;
`;

const BlockedLead = styled.span`
  color: ${({ theme }) => theme.colors.orangeExtraDark};
  font-weight: 500;
`;

const Steps = styled.ol`
  font-size: 12px;
  line-height: 20px;
  margin: 16px 0 0;
  padding-left: 20px;
`;

const ButtonRow = styled.div`
  margin-top: 16px;
`;

const DeviceRow = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
`;

const DeviceText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const NameRow = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
`;

const DeviceName = styled(Label)`
  color: ${({ theme }) => theme.colors.black100};
  font-size: 14px;
  font-weight: 500;
`;

const ThisDeviceBadge = styled(Label)`
  background-color: ${({ theme }) => theme.colors.black5};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.black100};
  padding: 2px 8px;
`;

const Status = styled(BodySmall)`
  color: ${({ theme }) => theme.colors.black50};
  font-size: 11px;
`;

const StatusText = styled.span<{ $receiving: boolean }>`
  color: ${({ $receiving, theme }) => ($receiving ? theme.colors.green100 : theme.colors.red100)};
`;

const Separator = styled.span`
  margin: 0 6px;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.red100};
  cursor: pointer;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 500;
  padding: 0;

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;
